<script setup lang="ts">
import type { ApiProductPage,
  ApiProductHeadingBlock,
  ApiProductDescriptionBlock,
  ApiProductPriceBlock,
  ApiProductFeaturesBlock,
  ApiProductContentBlock } from '@/types';
import { getBlock } from '@/utils/getBlock';

const props = defineProps<{ product: ApiProductPage }>();

const heading = computed(() => getBlock<ApiProductHeadingBlock>(props.product.body, 'heading'));
const description = computed(() => getBlock<ApiProductDescriptionBlock>(props.product.body, 'description'));
const price = computed(() => getBlock<ApiProductPriceBlock>(props.product.body, 'price'));
const features = computed(() => getBlock<ApiProductFeaturesBlock>(props.product.body, 'features'));
const content = computed(() => getBlock<ApiProductContentBlock>(props.product.body, 'content'));
</script>

<template>
  <div>
    <section class="py-16 md:py-24">
      <div class="max-w-6xl mx-auto px-6">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <!-- Left: Product details -->
          <div class="lg:col-span-3">
            <div class="flex items-center gap-5 mb-2">
              <div
                v-if="$slots.illustration"
                class="hidden sm:block shrink-0 w-20 h-20 text-primary-600/70"
              >
                <slot name="illustration" />
              </div>
              <h1
                v-if="heading"
                class="text-3xl md:text-4xl font-bold"
              >
                {{ heading.value }}
              </h1>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
            <div
              v-if="description"
              class="mt-6 text-[var(--color-charcoal)]/70 cms-prose"
              v-html="description.value"
            />

            <div
              v-if="price"
              class="mt-8 bg-white rounded-2xl p-6 shadow-sm inline-block"
            >
              <p class="text-sm font-bold uppercase tracking-wider text-[var(--color-peach)]">
                Pris
              </p>
              <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
              <div
                class="text-3xl font-bold tabular-nums mt-1 [&>p]:m-0"
                v-html="price.value"
              />
            </div>

            <div
              v-if="features?.value?.length"
              class="mt-8"
            >
              <ul class="space-y-3">
                <li
                  v-for="feature in features.value"
                  :key="feature.heading"
                  class="flex items-center gap-3"
                >
                  <span class="flex items-center justify-center size-7 rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <UIcon
                      name="i-heroicons-check"
                      class="size-4"
                    />
                  </span>
                  <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
                  <span v-html="feature.heading" />
                </li>
              </ul>
            </div>

            <div
              v-if="content?.value?.length"
              class="mt-12 space-y-6"
            >
              <UCard
                v-for="item in content.value"
                :key="item.id"
                class="bg-white rounded-2xl"
              >
                <h2 class="font-semibold text-base">
                  {{ item.value.heading }}
                </h2>
                <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
                <div
                  class="mt-2 text-sm text-[var(--color-charcoal)]/70 cms-prose"
                  v-html="item.value.description"
                />
              </UCard>
            </div>
          </div>

          <!-- Right: Signup form -->
          <div class="lg:col-span-2">
            <div class="lg:sticky lg:top-24 bg-white rounded-2xl p-6 md:p-8 shadow-[0_2px_24px_rgba(45,41,38,0.06)] border border-[var(--color-warm-border)]/50">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
